import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef } from 'react';

const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-1.5',
    'font-medium tracking-tight whitespace-nowrap select-none',
    'rounded-2 border',
    'transition-colors duration-micro ease-out',
    'focus-visible:outline-none focus-visible:shadow-focus',
    'disabled:cursor-not-allowed',
  ].join(' '),
  {
    variants: {
      variant: {
        primary: [
          'bg-accent-500 border-accent-500 text-accent-fg',
          'hover:bg-accent-600 hover:border-accent-600',
          'active:bg-accent-700 active:border-accent-700',
          'disabled:bg-accent-200 disabled:border-accent-200 disabled:text-accent-fg/60',
        ].join(' '),
        secondary: [
          'bg-bg-panel border-border-strong text-fg-primary',
          'hover:bg-bg-hover',
          'active:bg-bg-active',
          'disabled:text-fg-disabled disabled:border-border-default',
        ].join(' '),
        ghost: [
          'bg-transparent border-transparent text-fg-secondary',
          'hover:bg-bg-hover hover:text-fg-primary',
          'active:bg-bg-active',
        ].join(' '),
        destructive: [
          'bg-bg-panel border-border-default text-error',
          'hover:bg-error-subtle hover:border-error',
        ].join(' '),
      },
      size: {
        sm: 'h-control-sm px-2.5 text-12',
        md: 'h-control px-3 text-13',
        lg: 'h-control-lg px-4 text-14',
        icon: 'h-control w-control px-0',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, ...props }, ref) => (
    <button
      ref={ref}
      className={buttonVariants({ variant, size, className })}
      {...props}
    />
  ),
);
Button.displayName = 'Button';
